import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hook/redux";


function AlignSelector( { canvas, object, property, buttonText } ) {
  const { selectedLayout, scale, layouts, qrCode } = useAppSelector( ( state ) => state.editor );

  const [ selectedXAlign, setSelectedXAlign ] = useState( object.textAlign );
  const [ selectedYAlign, setSelectedYAlign ] = useState( object.originY );

  const dispatch = useAppDispatch();

  const activeObject = canvas.current.getActiveObject();

  const activeLayout = layouts.find( ( layout ) => layout.id === selectedLayout );
  const activeElement = activeLayout && activeLayout.elements.find( ( element ) => element.imgIdx === activeObject.idx );
  const initialTop =activeElement ? parseFloat(activeElement.style.top) * scale : 0

  /**
   * Updates the vertical alignment of an object on the canvas based on the current selection.
   *
   * This function recalculates the top position of the object based on the `selectedYAlign` value,
   * adjusts the object's origin, and sets a new alignment state. It also updates the canvas rendering
   * and dispatches an action to update the current fabric data in the editor.
   *
   * Behavior:
   * - If `selectedYAlign` is "center", it sets the object's alignment to "bottom" and moves it accordingly.
   * - If `selectedYAlign` is "bottom", it sets the object's alignment to "top"*/
  const handleYAlignChange = () => {
    const canvasHeight = object.maxHeight;
    const qrCodeHeight = 150 * scale;
    let newCanvasHeight = qrCode ? canvasHeight - qrCodeHeight : canvasHeight;

    if(initialTop){
      switch (selectedYAlign) {
        case "center":
          object.set({ top: initialTop + newCanvasHeight, originY: "bottom" });
          setSelectedYAlign("bottom");
          break;
        case "bottom":
          object.set({ top: initialTop, originY: "top"});
          setSelectedYAlign("top");
          break;
        case "top":
          object.set({ top: initialTop + newCanvasHeight / 2  , originY: "center"});
          setSelectedYAlign("center");
          break;
        default:
          break;
      }
    }

    canvas.current.renderAll();

    dispatch( {
      type: "editor/updateCurrentDataFabricArray",
      payload: {
        layoutId: selectedLayout,
        fabricElements: JSON.stringify(
          canvas.current.toJSON()
        )
      }
    } );
  };

  /**
   * A function that handles changes to the horizontal text alignment of an object.
   * It cycles through the alignment values "center", "left", and "right".
   * Updates the selected horizontal alignment state, the object's alignment property,
   * re-renders the canvas, and dispatches an action to update the current canvas data.
   *
   * The function performs the following steps:
   * 1. Checks the current value of `selectedXAlign` and sets the next alignment based on the current value:
   *    - "center" changes to "right".
   *    - "left" changes to "center".
   *    - "right" changes to "left".
   * 2. Updates the alignment of the object on the canvas using the `object.set` method.
   * 3. Re-renders the canvas to reflect the alignment changes.
   * 4. Dispatches an action to synchronize the updated canvas data (`fabricElements`)
   *    in the context of the current layout.
   *
   * No parameters or arguments are passed, and no value is returned. The function relies
   * on external variables and functions such as `selectedXAlign`, `setSelectedXAlign`,
   * `object`, `canvas`, and `dispatch`.
   */
  const handleXAlignChange = () => {
    switch ( selectedXAlign ) {
      case "center":
        setSelectedXAlign( "right" );
        object.set( "textAlign", "right" );
        break;
      case "left":
        setSelectedXAlign( "center" );
        object.set( "textAlign", "center" );
        break;
      case "right":
        setSelectedXAlign( "left" );
        object.set( "textAlign", "left" );
        break;
      default:
        break;
    }

    canvas.current.renderAll();

    dispatch( {
      type: "editor/updateCurrentDataFabricArray",
      payload: {
        layoutId: selectedLayout,
        fabricElements: JSON.stringify(
          canvas.current.toJSON()
        )
      }
    } );
  };

  /**
   * Returns a JSX element representing an icon for the specified text alignment.
   *
   * @param {string} alignment - The alignment type. Possible values are 'left', 'center', or others.
   * @returns {JSX.Element} A JSX element containing an icon corresponding to the given alignment:
   *                        - 'left': Left alignment*/
  const getAlignmentIcon = ( alignment: string ): JSX.Element => {
    switch ( alignment ) {
      case 'left':
        return <i className="fas fa-align-left"></i>;
      case 'center':
        return <i className="fas fa-align-center"></i>;
      default:
        return <i className="fas fa-align-right"></i>;
    }
  };

  /**
   * Returns a JSX element representing a vertical alignment icon based on the provided alignment value.
   *
   * @param {string} alignment - The vertical alignment type. Possible values are "top", "center", or any other string for the default case.
   * @returns {JSX.Element} A JSX element containing an icon corresponding to the alignment type.
   */
  const getVAlignmentIcon = ( alignment: string ): JSX.Element => {
    switch ( alignment ) {
      case "top":
        return <i className="fas fa-bars pb-3"></i>;
      case "center":
        return <i className="fas fa-bars"></i>;
      default:
        return <i className="fas fa-bars pt-3"></i>;
    }
  };

  const horizontalAlignment = object && getAlignmentIcon( object.textAlign );
  let verticalAlignment = object && getVAlignmentIcon( object.originY );

  return (
    <div className="toolbar-button"
         onClick={ property === "originY" ? handleYAlignChange : handleXAlignChange }
         data-test={ "text-valign" }>
      { buttonText === "VAlignSelector" ? verticalAlignment :
        horizontalAlignment
      }
    </div>
  );
}

export default AlignSelector;
